<template>
  <label
    :class="[b(modifiers), $attrs.class]"
    @mouseenter="hover = true"
    @mouseleave="hover = false"
  >
    <input
      v-model="internalValue"
      v-bind="$attrs"
      :class="b('field')"
      :value="value"
      :name="name"
      type="checkbox"
      :checked="isChecked"
      @blur="onBlur"
      @focus="onFocus"
    />
    <span :class="b('indicator')"></span>
    <span :class="b('label-text')">
      <slot></slot>
    </span>
  </label>
</template>

<script lang="ts">
  import { PropType, defineComponent, toRefs } from 'vue';
  import useFormStates, { FormStates, withProps } from '@/compositions/form-states';
  import { Modifiers } from '@/plugins/vue-bem-cn/src/globals';

  /**
   * Checkbox component for form elements.
   * Can be used as single element with a Boolean value or multiple checkboxes with an Array.
   */
  export default defineComponent({
    name: 'e-checkbox',

    inheritAttrs: false,

    props: {
      ...withProps(),

      /**
       * The model value to be used for v-model.
       */
      modelValue: {
        type: [Boolean, Array] as PropType<any>, // eslint-disable-line @typescript-eslint/no-explicit-any -- was not able to find a better solution, since the value is very dynamic
        required: true,
      },

      /**
       * Adds name attribute.
       */
      name: {
        type: String,
        required: true,
      },

      /**
       * Adds value attribute.
       */
      value: {
        type: [String, Number, Boolean] as PropType<any>, // eslint-disable-line @typescript-eslint/no-explicit-any -- was not able to find a better solution, since the value is very dynamic
        required: true,
      },

      /**
       * Allows to set the desired layout variant.
       */
      variant: {
        type: String,
        default: 'default',
        validator: (value: string) => ['default', 'toggle'].includes(value),
      },
    },

    emits: {
      'update:modelValue': (payload: unknown): boolean => typeof payload !== 'undefined',
      'change': (payload: unknown): boolean => typeof payload !== 'undefined',
      'focus': (): boolean => true,
      'blur': (): boolean => true,
    },

    setup(props): FormStates {
      return {
        ...useFormStates(toRefs(props).state),
      };
    },

    // data() {
    //   return {};
    // },

    computed: {
      /**
       * Returns a configuration Object for modifier classes.
       */
      modifiers(): Modifiers {
        return {
          ...this.stateModifiers,
          variant: this.variant,
        };
      },

      /**
       * Sets value of component model to parent model
       */
      internalValue: {
        get(): boolean | string[] | number[] {
          return this.modelValue;
        },
        set(value: boolean | string[] | number[]) {
          /**
           * Emits checkbox value e.g. true/false or value
           */
          this.$emit('update:modelValue', value);

          // event needed for the multiselect
          this.$emit('change', value);
        },
      },

      /**
       * Evaluates if the checkbox is currently selected.
       */
      isChecked() {
        return Array.isArray(this.value) ? this.value.includes(this.modelValue) : this.modelValue;
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Emits focus to parent and wrapper component.
       * Update "hasFocus" state.
       */
      onFocus() {
        this.focus = true;

        /**
         * Focus event
         */
        this.$emit('focus');
      },

      /**
       * Emits blur to parent and wrapper component.
       * Update "hasFocus" state.
       */
      onBlur() {
        this.focus = false;

        /**
         * Blur event.
         */
        this.$emit('blur');
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use 'sass:math';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/variables';

  .e-checkbox {
    $this: &;
    $label-size: 1.3rem;
    $border-width: 2px;

    @include mixins.hover-effect--default();

    position: relative;
    display: block;
    cursor: pointer;
    font-size: variables.$font-size--regular;

    &__field {
      position: absolute;
      left: -200vw;
      -webkit-appearance: none;
    }

    &--variant-default {
      display: flex;
      align-items: center;

      #{$this}__indicator {
        position: relative;
        display: flex;
        flex: 0 0 auto;
        align-items: center;
        width: $label-size;
        height: $label-size;
        border: $border-width solid variables.$va-color-grayscale--800;
        border-radius: variables.$form-field--border-radius;
        background: variables.$va-color-grayscale--1000;

        &::before {
          content: '✓';
          position: absolute;
          inset: auto;
          width: calc($label-size - $border-width);
          height: calc($label-size - $border-width);
          opacity: 0;
          color: variables.$va-color-grayscale--0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding-top: $border-width;
          transform: scale(0);
          transition-timing-function: ease-in-out;
          transition-duration: 50ms;
          transition-property: opacity, transform;
        }
      }

      #{$this}__field:checked {
        ~ #{$this}__indicator::before {
          opacity: 1;
          transform: scale(1);
        }
      }
    }

    &--variant-toggle {
      $toggle--size: 1rem;

      display: flex;
      align-items: center;
      cursor: pointer;

      #{$this}__indicator {
        position: relative;
        width: 1.5 * $toggle--size;
        height: math.div($toggle--size, 3) * 2;
        margin: 0 math.div($toggle--size, 2);
        border-radius: $toggle--size;
        background-color: variables.$va-color-grayscale--500;
        transition: background-color variables.$va-transition-duration--300;

        &::before {
          position: absolute;
          top: 50%;
          left: math.div($toggle--size, -2);
          content: '';
          width: $toggle--size;
          height: $toggle--size;
          border-radius: 50%;
          background-color: variables.$va-color-grayscale--1000;
          box-shadow: 0 1px 2px 1px rgba(variables.$va-color-grayscale--0, 0.2);
          transform: translateY(-50%);
          transition: left variables.$va-transition-duration--300;
        }
      }

      #{$this}__field {
        &:checked ~ #{$this}__indicator {
          &::before {
            left: calc(100% - (#{$toggle--size} / 2));
            background-color: variables.$va-color-grayscale--0;
            transform: translateY(-50%);
          }
        }
      }
    }

    &__label-text {
      display: block;
      margin: 0;
      padding-left: variables.$va-spacing--10;

      &:hover {
        color: variables.$va-color-grayscale--0;

        &::before {
          border-color: variables.$va-color-grayscale--0;
        }
      }
    }

    &__field:checked {
      ~ #{$this}__indicator {
        border-color: var(--theme-color-primary--1);
      }

      ~ #{$this}__label-text {
        color: var(--theme-color-primary--1);
      }
    }

    &__field:checked:disabled,
    &__field:disabled {
      ~ #{$this}__indicator {
        border-color: variables.$va-color-grayscale--800;
        cursor: not-allowed;
      }

      ~ #{$this}__indicator:before {
        cursor: not-allowed;
        color: variables.$va-color-grayscale--600;
      }

      ~ #{$this}__label-text {
        cursor: not-allowed;
        color: variables.$va-color-grayscale--600;
      }
    }

    &__field:checked:disabled {
      ~ #{$this}__indicator,
      ~ #{$this}__indicator:before {
        border-color: variables.$va-color-grayscale--500;
        color: variables.$va-color-grayscale--500;
      }
    }
  }
</style>
